import type { AccountUser, InvitableRole, InviteLink } from '../../api/accounts';
import { formatDate } from '../../i18n/formats.ts';
import type { Locale } from '../../i18n/locale.ts';
import type { MessageKey } from '../../i18n/messages.en.ts';
import type { Message } from '../../i18n/translate.ts';

/**
 * MVP-226: everything about invites and account windows that is a decision rather than a request —
 * kept out of the screens so it is testable without a DOM (same split as `features/admin/ingestQueue.ts`).
 *
 * Dates cross this boundary in two shapes and only two: an ISO string on the wire, and the
 * `YYYY-MM-DD` a native `<input type="date">` speaks. An operator typing "1 Dec" means midnight
 * where they are standing, so the conversion is deliberately LOCAL time in both directions — a UTC
 * reading would silently move a Bangkok window seven hours and expire an account on the wrong day.
 */

export const INVITE_PATH_PREFIX = '/invite/';

/** The link an admin copies. Absolute, because it is going into a chat message on someone else's
 * phone. `origin` is passed in rather than read off `window` so this stays testable. */
export function inviteUrl(token: string, origin: string): string {
  return `${origin.replace(/\/$/, '')}${INVITE_PATH_PREFIX}${encodeURIComponent(token)}`;
}

/** `YYYY-MM-DD` in local time, for a date input's `value`. Empty string for "no bound". */
export function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The inverse: a date input's value back to an ISO instant at LOCAL midnight, or `null` for a
 * cleared field — which is how both the invite and account APIs spell "permanent". */
export function dateInputToIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** The default the invite form opens with: three months out, matching the server's own default
 * when `validUntil` is omitted entirely (`DEFAULT_WINDOW_MONTHS` in
 * `strapi-template/src/utils/account-window.js`). The form always SENDS a value, so the two only
 * have to agree on what a person expects to see, not on who wins. */
export function defaultValidUntilInput(now: Date = new Date()): string {
  const out = new Date(now.getTime());
  out.setMonth(out.getMonth() + 3);
  return isoToDateInput(out.toISOString());
}

/** MVP-291: was a module-level `Intl.DateTimeFormat(undefined, …)` built at import — one formatter
 * for the life of the tab, locked to the browser's locale and unable to follow a language switch.
 * `admin` surface: an account window is read against the audit log and the server's own ISO
 * timestamps, so Thai gets Thai month names on Gregorian years. */
function dateText(iso: string, locale: Locale): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : formatDate(date, locale, 'admin');
}

/**
 * One line describing an account's window, in the tense that is true right now: "Expired 1 Sep",
 * "From 1 Oct" for one that has not opened yet, "Until 1 Dec" for a live one, "Permanent" for no
 * bounds at all. `validUntil` is exclusive server-side (`evaluateAccountWindow`), so an account
 * whose bound is today reads as expired here too rather than as "until today".
 *
 * The DATE inside it is formatted here and the sentence around it is a dictionary key the render
 * site resolves. MVP-291: `locale` is the app's own, passed in rather than read off the browser —
 * that is the whole point of the task, and a module tested under node has no provider to ask.
 */
export function windowLabel(
  account: Pick<AccountUser, 'validFrom' | 'validUntil'>,
  locale: Locale,
  now: Date = new Date()
): Message {
  const from = account.validFrom ? new Date(account.validFrom) : null;
  const until = account.validUntil ? new Date(account.validUntil) : null;
  if (until && now >= until) return { key: 'admin.window.expired', params: { date: dateText(account.validUntil as string, locale) } };
  if (from && now < from) return { key: 'admin.window.from', params: { date: dateText(account.validFrom as string, locale) } };
  if (until) return { key: 'admin.window.until', params: { date: dateText(account.validUntil as string, locale) } };
  return { key: 'admin.window.permanent' };
}

export type InviteStatus = 'active' | 'used' | 'revoked' | 'expired';

/** Mirrors `evaluateInvite` server-side, in the order that matters: what actually happened to the
 * link wins over the clock, so a link that was used yesterday still reads "used" after it would
 * also have expired. */
export function inviteStatus(invite: InviteLink, now: Date = new Date()): InviteStatus {
  if (invite.usedAt) return 'used';
  if (invite.revokedAt) return 'revoked';
  if (invite.expiresAt && now >= new Date(invite.expiresAt)) return 'expired';
  return 'active';
}

export function inviteStatusLabel(invite: InviteLink, locale: Locale, now: Date = new Date()): Message {
  switch (inviteStatus(invite, now)) {
    case 'used':
      // The username is the person's own; only the sentence around it is copy. A link used by
      // an account this build cannot name gets its own sentence rather than "used by someone".
      return invite.usedByUsername
        ? { key: 'admin.invite.statusUsed', params: { name: invite.usedByUsername } }
        : { key: 'admin.invite.statusUsedUnknown' };
    case 'revoked':
      return { key: 'admin.invite.statusRevoked' };
    case 'expired':
      return { key: 'admin.invite.statusExpired' };
    default:
      return invite.expiresAt
        ? { key: 'admin.invite.statusValidUntil', params: { date: dateText(invite.expiresAt, locale) } }
        : { key: 'admin.invite.statusActive' };
  }
}

/** MVP-272 Phase 6: the role's own words are dictionary keys, so the invite form, the account rows
 * and the audit log (`audit.ts` reads this same list) can never disagree about what a role is
 * called in either language. */
export const INVITABLE_ROLES: { value: InvitableRole; labelKey: MessageKey; hintKey: MessageKey }[] = [
  { value: 'authenticated', labelKey: 'admin.role.customer', hintKey: 'admin.role.customerHint' },
  { value: 'shop', labelKey: 'admin.role.shop', hintKey: 'admin.role.shopHint' },
  { value: 'agency', labelKey: 'admin.role.agency', hintKey: 'admin.role.agencyHint' },
  { value: 'downloader', labelKey: 'admin.role.downloader', hintKey: 'admin.role.downloaderHint' },
  { value: 'admin', labelKey: 'admin.role.admin', hintKey: 'admin.role.adminHint' },
  // MVP-585/MVP-654. Last, and away from the four operator rows: a rights agent has no room, no
  // queue and no library — the list should not read as "one more kind of KJ".
  { value: 'rights', labelKey: 'admin.role.rights', hintKey: 'admin.role.rightsHint' },
];

/**
 * MVP-560: which of the above an INVITE LINK may carry — a copy of the `roleType` enum in
 * `strapi-template/src/api/invite/content-types/invite/schema.json`, the column the invite row is
 * written to. `INVITABLE_ROLES` is every role this app can NAME (the account row's role editor and
 * the audit log read it too); this is every role it can ISSUE. MVP-464 added `shop` and `agency` to
 * the form and the controller's validation but not to that enum, so picking either produced a
 * server error out of a dropdown that had offered it.
 *
 * The two lists are the same set again as of MVP-655, and this one stays a separate constant all the
 * same. They answer different questions — `public` can be named and never issued, and the next role
 * to arrive will be nameable before its column exists, which is the exact shape of the MVP-464 bug.
 *
 * MVP-654 widens it, in the same commit as the enum and the deploy. `rights` is the reason: the
 * role and its report went live with MVP-585 and no rights agent could be given an account at all,
 * because this form is the only door in. `agency` rides along because it was missing for the same
 * reason and the next role would repeat it.
 *
 * MVP-655 adds the last one, `shop`, which MVP-654 held back because an invite could not name the
 * branch a new shop would own — it would have carried the issuing admin's default venue and the new
 * owner would have redeemed into the shop already trading there. The form now names it, so the list
 * is finally every row of `INVITABLE_ROLES`. `requiresVenue` below is the other half.
 *
 * `invites.test.ts` pins this against both the schema enum and `ROLE_TYPES` in
 * `strapi-template/src/utils/invite-view.js`, in `INVITABLE_ROLES` order.
 */
export const INVITE_ROLE_TYPES: InvitableRole[] = [
  'authenticated',
  'shop',
  'agency',
  'downloader',
  'admin',
  'rights',
];

/**
 * MVP-655: whether this role's invite has to name a branch before it can be issued.
 *
 * True for exactly one role and written as a predicate anyway, because the reason is not "shop is
 * special" but "a shop IS a venue". Every other role joins a venue that already exists around it —
 * the issuer's own, or none at all — and the server resolves that without being told. A shop has no
 * such venue to be resolved from, so the server refuses with `VENUE_REQUIRED` rather than falling
 * back, and this is what stops the form offering a Create button that cannot work.
 */
export function requiresVenue(roleType: InvitableRole): boolean {
  return roleType === 'shop';
}

/**
 * MVP-464: which of the above the signed-in issuer may actually mint. Mirrors `ISSUABLE_ROLE_TYPES`
 * in `strapi-template/src/api/invite/controllers/invite.js` — the server refuses anything else with
 * `ROLE_NOT_ISSUABLE`, so offering a fourth option to a shop would be offering a 403.
 *
 * Returns the entries in INVITABLE_ROLES order rather than its own, so the list a shop sees is the
 * admin's list with rows removed, never a differently-ordered one. An unknown or absent role gets
 * an empty list: the screen is gated by `canInviteStaff` before this is ever read, and "no role"
 * must never fall through to "every role".
 */
export function issuableRoles(roleType: string | null | undefined) {
  const allowed: Record<string, InvitableRole[]> = {
    // The full `INVITE_ROLE_TYPES` — an admin is the only issuer that may mint every role an invite
    // can carry, which since MVP-655 is every row of `INVITABLE_ROLES`.
    admin: [...INVITE_ROLE_TYPES],
    shop: ['authenticated'],
  };
  const values = allowed[roleType ?? ''] ?? [];
  return INVITABLE_ROLES.filter((r) => values.includes(r.value));
}

/**
 * MVP-510 slice 4: whether the staff roster shows a branch column.
 *
 * A single-branch shop — which is most of them — would otherwise get a column repeating the same
 * name on every row, and the elderly-friendly rule that governs this UI is that a screen carries as
 * few things as the question needs. Two or more DISTINCT branches in the list and the column is the
 * only thing telling two identically-named KJs apart, so it appears.
 *
 * Counted over `venueId`, not `venueName`: the id is what the server scoped the list by, and two
 * branches are allowed to be called the same thing. A null id (an account minted before slice 1, so
 * attached to no branch) is not a branch and is not counted — one such row beside one real branch
 * must not turn the column on, because the column would have nothing to say about it.
 */
export function showsBranchColumn(staff: { venueId: number | null }[]): boolean {
  return new Set(staff.map((s) => s.venueId).filter((id) => id != null)).size > 1;
}

/**
 * The redeem form's own validation, deliberately the same three rules the server enforces
 * (`invite.redeem`) rather than looser ones — a rule only the server knows becomes a 400 the person
 * cannot act on, and a rule only the client knows is not a rule at all. Returns the first problem,
 * or null when the form may be submitted.
 */
export function redeemFormError(fields: {
  username: string;
  email: string;
  password: string;
  confirm: string;
}): MessageKey | null {
  if (fields.username.trim().length < 3) return 'auth.redeem.usernameTooShort';
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(fields.email.trim())) return 'auth.redeem.emailInvalid';
  if (fields.password.length < 10) return 'auth.redeem.passwordTooShort';
  if (fields.password !== fields.confirm) return 'auth.redeem.passwordMismatch';
  return null;
}
